import React, { useState, useEffect, useRef } from "react";
import api from "../../api.ts";
import toast from "react-hot-toast";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import { TiptapMenuBar } from "../editor/TiptapEditor.tsx";
import Modal from "../ui/Modal.tsx";
import { COMPANY_OPTIONS } from "../ui/CompanyFilter.tsx";
import { FiSearch, FiX } from "react-icons/fi";

interface Notice {
  id: number;
  message: string;
  created_at: string;
}

interface InternalUser {
  id: number;
  nome: string;
  cargo?: string;
  setor?: string;
}

interface NoticeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  noticeToEdit?: Notice | null;
  companySlug?: string;
}

const NoticeModal: React.FC<NoticeModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  noticeToEdit,
  companySlug,
}) => {
  // Empresa-alvo do aviso ("all" = global, visível para todas as empresas).
  const [company, setCompany] = useState<string>(companySlug || "all");
  const [sendEmail, setSendEmail] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [internalUsers, setInternalUsers] = useState<InternalUser[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [userSearch, setUserSearch] = useState("");
  const emojiPickerRef = useRef<HTMLDivElement>(null);

  // Ao (re)abrir o modal, alinha a empresa-alvo com o filtro atual da tela.
  useEffect(() => {
    if (isOpen) setCompany(companySlug || "all");
  }, [isOpen, companySlug]);

  const editor = useEditor({
    extensions: [StarterKit],
    content: "",
    immediatelyRender: false,
    editorProps: { attributes: { class: "tiptap-editor" } },
  });

  // Carrega usuários internos ao abrir o modal
  useEffect(() => {
    if (!isOpen) return;
    api
      .get("/api/notices/internal-users")
      .then((res) => setInternalUsers(res.data))
      .catch(() => toast.error("Erro ao carregar usuários."));
  }, [isOpen]);

  // Preenche dados ao editar
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;

    const applyEditorContent = () => {
      if (!editor || editor.isDestroyed) return;

      if (noticeToEdit) {
        editor.commands.setContent(noticeToEdit.message, { emitUpdate: false });
        setSendEmail(false);

        if (noticeToEdit.id) {
          api
            .get(`/api/notices/${noticeToEdit.id}/recipients`)
            .then((res) => setSelectedUserIds(res.data))
            .catch(() => {});
        }
      } else {
        editor.commands.clearContent();
        setSelectedUserIds([]);
        setSendEmail(false);
      }
      setShowEmojiPicker(false);
      setUserSearch("");
    };

    const timeoutId = window.setTimeout(applyEditorContent, 0);
    return () => window.clearTimeout(timeoutId);
  }, [noticeToEdit, editor, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target as Node)
      ) {
        setShowEmojiPicker(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  if (!isOpen) return null;

  const onEmojiClick = (emojiData: EmojiClickData) => {
    if (editor) editor.chain().focus().insertContent(emojiData.emoji).run();
    setShowEmojiPicker(false);
  };

  const toggleUser = (userId: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const selectAll = () => setSelectedUserIds(filteredUsers.map((u) => u.id));
  const clearAll = () => setSelectedUserIds([]);

  const filteredUsers = internalUsers.filter(
    (u) =>
      u.nome.toLowerCase().includes(userSearch.toLowerCase()) ||
      (u.cargo || "").toLowerCase().includes(userSearch.toLowerCase()) ||
      (u.setor || "").toLowerCase().includes(userSearch.toLowerCase()),
  );

  // A lista de usuários é exibida para empresas internas e some para V-PARTNER
  const showUserList = company !== "v-partner";

  const handleSubmit = async () => {
    if (!editor || editor.isEmpty) {
      toast.error("O aviso não pode estar em branco.");
      return;
    }
    const message = editor.getHTML();
    const payload = {
      message,
      sendEmail,
      company,
      // Só envia selectedUserIds se a lista estiver visível
      selectedUserIds: showUserList ? selectedUserIds : [],
    };

    try {
      if (noticeToEdit) {
        await api.put(`/api/notices/${noticeToEdit.id}`, payload);
        toast.success("Aviso atualizado com sucesso!");
      } else {
        if (sendEmail) toast.loading("Postando aviso e enviando e-mails...");
        await api.post("/api/notices/", payload);
        toast.dismiss();
        toast.success("Aviso postado com sucesso!");
      }
      onSuccess();
      onClose();
    } catch (err) {
      toast.dismiss();
      toast.error("Ocorreu um erro ao guardar o aviso.");
    }
  };

  const categoryConfig = [
    { category: "suggested", name: "Sugeridos" },
    { category: "smileys_people", name: "Expressões e Pessoas" },
    { category: "animals_nature", name: "Animais e Natureza" },
    { category: "food_drink", name: "Comidas e Bebidas" },
    { category: "travel_places", name: "Viagens e Lugares" },
    { category: "activities", name: "Atividades" },
    { category: "objects", name: "Objetos" },
    { category: "symbols", name: "Símbolos" },
    { category: "flags", name: "Bandeiras" },
  ];

  return (
    <Modal onClose={onClose} title={noticeToEdit ? "Editar Aviso" : "Adicionar Novo Aviso"}>
        {/* Editor */}
        <div className="form-row">
          <div className="tiptap-container">
            <TiptapMenuBar
              editor={editor}
              onEmojiToggle={() => setShowEmojiPicker((prev) => !prev)}
            />
            <EditorContent editor={editor} />
            {showEmojiPicker && (
              <div
                ref={emojiPickerRef}
                className="emoji-picker-container-modal"
              >
                <EmojiPicker
                  onEmojiClick={onEmojiClick}
                  width="100%"
                  height={350}
                  previewConfig={{ showPreview: false }}
                  categories={categoryConfig}
                />
              </div>
            )}
          </div>
        </div>

        {/* Empresa-alvo (apenas na criação; o PUT não altera a empresa) */}
        {!noticeToEdit && (
          <>
            <div className="form-row">
              <label htmlFor="notice-company">Empresa:</label>
            </div>
            <div className="form-row">
              <select
                id="notice-company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="form-input"
              >
                {COMPANY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.value === "all" ? "Todas as empresas (global)" : opt.label}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {/* Seletor de destinatários (some para V-PARTNER) */}
        {showUserList && (
          <>
            <div className="form-row">
              <label>
                Destinatários para e-mail:
                <span className="text-span">
                  {selectedUserIds.length === 0
                    ? "Todos os destinatários da empresa selecionada"
                    : `${selectedUserIds.length} usuário(s) selecionado(s)`}
                </span>
              </label>
            </div>

            {/* Barra de busca + ações */}
            <div className="form-row">
              <div>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Buscar por nome, cargo ou setor..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />
              </div>
              <button
                type="button"
                className="form-button-cancel"
                onClick={selectAll}
              >
                Todos
              </button>
              <button
                type="button"
                className="form-button-cancel"
                onClick={clearAll}
              >
                Limpar
              </button>
            </div>

            {/* Lista de usuários */}
            <div className="users-list">
              {filteredUsers.length === 0 ? (
                <p>Nenhum usuário encontrado.</p>
              ) : (
                filteredUsers.map((u) => {
                  const isSelected = selectedUserIds.includes(u.id);
                  return (
                    <div
                      key={u.id}
                      onClick={() => toggleUser(u.id)}
                      className={`users-list-option-details notice-recipient-option${
                        isSelected ? " selected" : ""
                      }`}
                    >
                      <div className="users-list-options">
                        <span>{u.nome}</span>
                        {(u.cargo || u.setor) && (
                          <span className="notice-recipient-meta">
                            {[u.cargo, u.setor].filter(Boolean).join(" · ")}
                          </span>
                        )}
                      </div>
                      {isSelected && <FiX />}
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        {/* Envio por e-mail */}
        {!noticeToEdit && (
          <div className="form-row notice-email-row">
            <input
              type="checkbox"
              id="sendEmail"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="notice-email-checkbox"
            />
            <label htmlFor="sendEmail" className="notice-email-label">
              {showUserList && selectedUserIds.length > 0
                ? `Enviar aviso por e-mail para os ${selectedUserIds.length} selecionados`
                : "Enviar aviso por e-mail para todos"}
            </label>
          </div>
        )}

        <div className="modal-actions">
          <button
            type="button"
            onClick={onClose}
            className="form-button-cancel"
          >
            Cancelar
          </button>
          <button type="button" onClick={handleSubmit} className="form-button">
            Salvar
          </button>
        </div>
    </Modal>
  );
};

export default NoticeModal;
